"use client";

import { useEffect, useRef, useState, type PointerEvent } from "react";
import Image from "next/image";
import { Play, ZoomIn } from "lucide-react";

export type InfiniteDraggableSlide = {
  imageSrc: string;
  imageAlt: string;
  title?: string;
};

/** Affordance shown on interactive slides: play button or zoom icon. */
export type SlideInteraction = "play" | "zoom";

/**
 * Controls the slide card's box shape so it matches the source images'
 * aspect ratio for a given category — every card in a slider still shares
 * the same size, but the size itself is tuned per category so the full
 * picture fits without being cropped.
 *
 * - "tall": default portrait card used for video trailers / author sites.
 * - "portrait": book-cover style ratio (matches book-illustration assets).
 * - "square": 1:1 ratio (matches book-marketing assets).
 */
export type SlideCardVariant = "tall" | "portrait" | "square";

export type InfiniteDraggableSliderProps = {
  slides: InfiniteDraggableSlide[];
  className?: string;
  onSlideClick?: (slideIndex: number) => void;
  interaction?: SlideInteraction;
  cardVariant?: SlideCardVariant;
};

const CARD_VARIANT_CLASSES: Record<SlideCardVariant, string> = {
  tall: "h-[480px] w-[220px] sm:h-[560px] sm:w-[260px] lg:w-[290px]",
  portrait: "h-[352px] w-[220px] sm:h-[416px] sm:w-[260px] lg:h-[464px] lg:w-[290px]",
  square: "h-[220px] w-[220px] sm:h-[260px] sm:w-[260px] lg:h-[290px] lg:w-[290px]",
};

type DragState = {
  startX: number;
  startTranslateX: number;
};

const CLICK_THRESHOLD_PX = 8;

function normalizeTranslateX(value: number, cycleWidth: number) {
  if (cycleWidth <= 0) return value;

  let nextValue = value;
  const minTranslate = -cycleWidth * 2;
  const maxTranslate = -cycleWidth;

  while (nextValue < minTranslate) {
    nextValue += cycleWidth;
  }

  while (nextValue > maxTranslate) {
    nextValue -= cycleWidth;
  }

  return nextValue;
}

function SlideCard({
  slide,
  slideIndex,
  isInteractive,
  interaction,
  cardVariant,
  onActivate,
}: {
  slide: InfiniteDraggableSlide;
  slideIndex: number;
  isInteractive: boolean;
  interaction: SlideInteraction;
  cardVariant: SlideCardVariant;
  onActivate?: (slideIndex: number) => void;
}) {
  const [hasError, setHasError] = useState(false);

  const handleActivate = () => {
    onActivate?.(slideIndex);
  };

  const interactionLabel =
    interaction === "zoom"
      ? slide.title
        ? `View ${slide.title} full size`
        : "View full-size image"
      : slide.title
        ? `Play ${slide.title} trailer`
        : "Play video trailer";

  return (
    <div
      data-slide-index={slideIndex}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? interactionLabel : undefined}
      onClick={isInteractive ? handleActivate : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleActivate();
              }
            }
          : undefined
      }
      className={[
        "group relative shrink-0 overflow-hidden rounded-2xl bg-[#1f2914] shadow-[0_18px_45px_rgba(0,0,0,0.35)] transition-transform duration-500 ease-out hover:-translate-y-1",
        CARD_VARIANT_CLASSES[cardVariant],
      ].join(" ")}
    >
      {hasError ? (
        <div className="flex h-full w-full items-center justify-center bg-linear-to-br from-[#29361a] to-[#1f2914] text-center text-white/70">
          <span className="px-4 text-xs font-semibold uppercase tracking-[0.2em]">
            {slide.title ?? slide.imageAlt}
          </span>
        </div>
      ) : (
        <>
          <Image
            src={slide.imageSrc}
            alt={slide.imageAlt}
            fill
            sizes="(max-width: 640px) 220px, (max-width: 1024px) 260px, 290px"
            loading="lazy"
            fetchPriority="low"
            className={[
              "transition-transform duration-700 ease-out group-hover:scale-[1.02]",
              cardVariant === "tall" ? "object-cover object-top" : "object-cover object-center",
            ].join(" ")}
            draggable={false}
            onDragStart={(event) => event.preventDefault()}
            onError={() => setHasError(true)}
          />
          <div className="absolute inset-0 bg-linear-to-b from-black/10 via-transparent to-black/18" />
        </>
      )}

      {isInteractive ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/10"
          aria-hidden="true"
        >
          <span
            className="play-btn"
            style={{ ["--glow-size" as string]: "22px" }}
          >
            {interaction === "zoom" ? (
              <ZoomIn className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Play className="ml-1 h-5 w-5 fill-current" aria-hidden="true" />
            )}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function InfiniteDraggableSlider({
  slides,
  className,
  onSlideClick,
  interaction = "play",
  cardVariant = "tall",
}: InfiniteDraggableSliderProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const suppressClickRef = useRef(false);
  const translateXRef = useRef(0);
  const cycleWidthRef = useRef(0);
  const [cycleWidth, setCycleWidth] = useState(0);

  const applyTranslateX = (value: number) => {
    const nextValue = normalizeTranslateX(value, cycleWidthRef.current);
    translateXRef.current = nextValue;

    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${nextValue}px, 0, 0)`;
    }
  };

  useEffect(() => {
    const element = measureRef.current;

    if (!element) return;

    // Prefer ResizeObserver contentRect — avoids forced reflow from
    // getBoundingClientRect() after style/DOM writes.
    const applyWidth = (nextWidth: number) => {
      if (nextWidth <= 0) return;

      cycleWidthRef.current = nextWidth;
      setCycleWidth(nextWidth);
      applyTranslateX(-nextWidth);
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      applyWidth(width);
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [slides.length]);

  useEffect(() => {
    const speedInPixelsPerSecond = 42;

    const step = (time: number) => {
      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = time;
      }

      const deltaMs = Math.min(time - lastFrameTimeRef.current, 32);
      lastFrameTimeRef.current = time;

      if (!isDraggingRef.current && cycleWidthRef.current > 0) {
        const deltaX = (speedInPixelsPerSecond * deltaMs) / 1000;
        applyTranslateX(translateXRef.current - deltaX);
      }

      animationFrameRef.current = window.requestAnimationFrame(step);
    };

    animationFrameRef.current = window.requestAnimationFrame(step);

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = null;
      lastFrameTimeRef.current = null;
    };
  }, [cycleWidth]);

  const releasePointer = () => {
    if (
      pointerIdRef.current !== null &&
      viewportRef.current?.hasPointerCapture(pointerIdRef.current)
    ) {
      viewportRef.current.releasePointerCapture(pointerIdRef.current);
    }

    pointerIdRef.current = null;
    dragStateRef.current = null;
    isDraggingRef.current = false;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (cycleWidthRef.current <= 0) return;
    if (event.button !== 0) return;

    pointerIdRef.current = event.pointerId;
    didDragRef.current = false;
    isDraggingRef.current = false;
    dragStateRef.current = {
      startX: event.clientX,
      startTranslateX: translateXRef.current,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current || cycleWidthRef.current <= 0) return;
    if (pointerIdRef.current !== event.pointerId) return;

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (!didDragRef.current) {
      if (Math.abs(deltaX) < CLICK_THRESHOLD_PX) return;

      didDragRef.current = true;
      isDraggingRef.current = true;
      viewportRef.current?.setPointerCapture(event.pointerId);
    }

    applyTranslateX(dragStateRef.current.startTranslateX + deltaX);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;

    const wasDrag = didDragRef.current;
    releasePointer();
    applyTranslateX(translateXRef.current);

    if (wasDrag) {
      suppressClickRef.current = true;
    }
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    if (didDragRef.current) {
      suppressClickRef.current = true;
    }
    releasePointer();
    applyTranslateX(translateXRef.current);
  };

  const handleSlideActivate = (slideIndex: number) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    onSlideClick?.(slideIndex);
  };

  return (
    <div className={["relative", className].filter(Boolean).join(" ")}>
      <div
        ref={viewportRef}
        className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#223017] px-3 py-4 shadow-[0_20px_60px_rgba(0,0,0,0.28)] select-none touch-pan-y"
        role="region"
        aria-label="Infinite draggable slider. Drag to explore."
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onLostPointerCapture={() => {
          isDraggingRef.current = false;
          dragStateRef.current = null;
          pointerIdRef.current = null;
        }}
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-[#223017] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-[#223017] to-transparent" />

        <div
          ref={trackRef}
          className="flex items-stretch gap-4 will-change-transform"
          style={{ transform: "translate3d(0, 0, 0)" }}
        >
          {Array.from({ length: 3 }).map((_, copyIndex) => (
            <div
              key={`copy-${copyIndex}`}
              ref={copyIndex === 0 ? measureRef : undefined}
              className="flex shrink-0 gap-4"
            >
              {slides.map((slide, slideIndex) => (
                <SlideCard
                  key={`${copyIndex}-${slideIndex}-${slide.imageSrc}`}
                  slide={slide}
                  slideIndex={slideIndex}
                  isInteractive={Boolean(onSlideClick)}
                  interaction={interaction}
                  cardVariant={cardVariant}
                  onActivate={onSlideClick ? handleSlideActivate : undefined}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
